// # Notice — 판 안의 알림 한 줄(docs/design.md)
//
// 오류·경고·귀띔·끝남을 **같은 자리에 같은 모양으로** 말한다. 판마다 `.trouble` 을 따로 그리면
// 여백·글자 크기·아이콘이 조금씩 갈리고, 사람은 그 차이를 "다른 종류의 문제" 로 읽는다.
//
// - 뜻은 색만으로 말하지 않는다. 아이콘(읽히지 않음)과 글자가 같이 간다.
// - `danger`·`warn` 은 `role="alert"`(끼어듦), `info`·`ok` 는 `role="status"`(공손) — 한 번만 읽힌다.
// - `plain` 은 바탕 없는 글자 한 줄이다. 칸 바로 밑·메뉴 옆처럼 판이 무거운 자리에 쓴다.
// - 손(`action`)은 끝에 붙는다 — "다시"·"닫기" 하나쯤. 알림을 닫을지는 부르는 쪽이 정한다.
//
// 떠서 사라지는 알림은 `Toast`, 칸 하나의 오류는 `Field` 의 `error` 다. 이것은 그 사이 — 판에 머무는 한 줄.

import { Check, CircleAlert, Info, TriangleAlert } from 'lucide-react'
import type { ReactNode } from 'react'
import { cx } from './cx.ts'

export type NoticeTone = 'danger' | 'warn' | 'info' | 'ok'

export interface NoticeProps {
  /** 기본 `danger`. */
  tone?: NoticeTone
  /** 글자 크기. 기본 `md`(sm 글자). 카드·도크 안은 `sm`, 문 앞 판은 `lg`. */
  size?: 'sm' | 'md' | 'lg'
  /** 바탕 없이 글자만. */
  plain?: boolean
  /** 뜻 아이콘을 바꾼다. `null` 이면 없다. */
  icon?: ReactNode
  /** 끝에 붙는 손. */
  action?: ReactNode
  className?: string
  /** 칸이 `aria-describedby` 로 가리킬 때. */
  id?: string
  children: ReactNode
}

const ICON: Record<NoticeTone, typeof Check> = {
  danger: CircleAlert,
  warn: TriangleAlert,
  info: Info,
  ok: Check,
}

export function Notice(props: NoticeProps) {
  const tone = props.tone ?? 'danger'
  const size = props.size ?? 'md'
  const Mark = ICON[tone]
  const icon =
    props.icon === undefined ? <Mark size={size === 'lg' ? 16 : 14} aria-hidden /> : props.icon
  return (
    <div
      className={cx('Notice', props.className)}
      id={props.id}
      role={tone === 'danger' || tone === 'warn' ? 'alert' : 'status'}
      data-tone={tone}
      data-size={size}
      data-plain={props.plain || undefined}
    >
      {icon !== null && (
        <span className="Notice-icon" aria-hidden>
          {icon}
        </span>
      )}
      <span className="Notice-text">{props.children}</span>
      {props.action !== undefined && <span className="Notice-action">{props.action}</span>}
    </div>
  )
}
